import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material"
import { collection, query, where, onSnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { useNavigate } from "react-router-dom"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import dayjs from "dayjs"
import { IBusinessHoliday, isDateInHolidays } from "../../utils/businessHolidays"
import { HEALTH_CHECK_ITEMS } from "../../utils/healthCheckItems"
import { IEmployee } from "./Employees"

const MONTHS = [
  { value: "01", label: "1月" },
  { value: "02", label: "2月" },
  { value: "03", label: "3月" },
  { value: "04", label: "4月" },
  { value: "05", label: "5月" },
  { value: "06", label: "6月" },
  { value: "07", label: "7月" },
  { value: "08", label: "8月" },
  { value: "09", label: "9月" },
  { value: "10", label: "10月" },
  { value: "11", label: "11月" },
  { value: "12", label: "12月" },
]

interface HealthCheckData {
  id?: string
  employeeId: string
  employeeName: string
  email: string
  year: string
  month: string
  day: string
  date: string
  status?: string
  checks?: Record<string, string>
  remarks?: string
  updatedAt?: string
}

const HealthCheck = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const [employees, setEmployees] = useState<IEmployee[]>([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("")
  const [records, setRecords] = useState<Record<string, HealthCheckData>>({})
  const [holidays, setHolidays] = useState<IBusinessHoliday[]>([])
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().format("YYYY"))
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format("MM"))
  const [daysInMonth, setDaysInMonth] = useState<number>(31)

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "employees"), (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IEmployee))
      setEmployees(list)
      setSelectedEmployeeId(prev => {
        if (prev && list.some(e => e.id === prev)) return prev
        return list[0]?.id || ""
      })
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "businessHolidays"), (snapshot) => {
      setHolidays(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const days = dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth()
    setDaysInMonth(days)

    if (!selectedEmployeeId) {
      setRecords({})
      return
    }

    const healthChecksRef = collection(db, "healthChecks")
    const q = query(
      healthChecksRef,
      where("employeeId", "==", selectedEmployeeId),
      where("year", "==", selectedYear),
      where("month", "==", selectedMonth)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const recordsMap: Record<string, HealthCheckData> = {}
      snapshot.docs.forEach(doc => {
        const data = doc.data() as HealthCheckData
        recordsMap[data.day] = {
          id: doc.id,
          ...data
        }
      })
      setRecords(recordsMap)
    })

    return unsubscribe
  }, [selectedYear, selectedMonth, selectedEmployeeId])

  const getMonthLabel = (monthValue: string) => {
    const monthObj = MONTHS.find(m => m.value === monthValue)
    return monthObj ? monthObj.label : monthValue
  }

  const isMasterHoliday = (day: number) => {
    const dateStr = `${selectedYear}-${selectedMonth}-${day.toString().padStart(2, '0')}`
    return isDateInHolidays(dateStr, holidays)
  }

  const getCheckStatus = (day: number) => {
    const record = records[day.toString()]
    if (!record?.checks || record.status !== "completed") return null

    const hasIssue = HEALTH_CHECK_ITEMS.some(item => record.checks?.[item.key] === "×")
    return hasIssue ? "issue" : "ok"
  }

  const handleDownloadPdf = () => {
    if (!selectedEmployeeId) return
    navigate(`/admin/health-check/${selectedEmployeeId}/document?year=${selectedYear}&month=${selectedMonth}`)
  }

  const selectedEmployee = employees.find(e => e.id === selectedEmployeeId)

  return (
    <Box m={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          従業員健康チェック表
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>従業員</InputLabel>
            <Select
              value={selectedEmployeeId}
              label="従業員"
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
            >
              {employees.length === 0 && (
                <MenuItem value="" disabled>従業員が登録されていません</MenuItem>
              )}
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.name}{!emp.active && "（無効）"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>年</InputLabel>
            <Select
              value={selectedYear}
              label="年"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i).map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}年
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>月</InputLabel>
            <Select
              value={selectedMonth}
              label="月"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            disabled={!selectedEmployeeId}
          >
            PDFダウンロード
          </Button>
        </Box>
      </Box>

      {employees.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>
            従業員が登録されていません。先に「従業員マスタ」から従業員を登録してください。
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
                <TableCell sx={{ fontWeight: "bold" }}>日付</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>従業員名</TableCell>
                {HEALTH_CHECK_ITEMS.map(item => (
                  <TableCell key={item.key} sx={{ fontWeight: "bold" }}>{item.label}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: "bold" }}>状態</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>特記事項</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const record = records[day.toString()]
                const holiday = isMasterHoliday(day)
                const checkStatus = getCheckStatus(day)

                return (
                  <TableRow key={day} hover>
                    <TableCell>{selectedYear}年{getMonthLabel(selectedMonth)} {day}日</TableCell>
                    <TableCell>{holiday ? "-" : selectedEmployee?.name || "-"}</TableCell>
                    {holiday ? (
                      <TableCell colSpan={HEALTH_CHECK_ITEMS.length} align="center">
                        <Chip label="休業日" size="small" />
                      </TableCell>
                    ) : (
                      HEALTH_CHECK_ITEMS.map(item => (
                        <TableCell key={item.key} align="center">
                          {record?.checks?.[item.key] || ""}
                        </TableCell>
                      ))
                    )}
                    <TableCell>
                      {holiday ? (
                        <Chip label="休業" color="default" size="small" />
                      ) : !record ? (
                        <Chip label="未送信" color="default" size="small" />
                      ) : record.status !== "completed" ? (
                        <Chip label="未回答" color="warning" size="small" />
                      ) : checkStatus === "issue" ? (
                        <Chip label="要確認" color="error" size="small" />
                      ) : (
                        <Chip label="問題なし" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{holiday ? "" : record?.remarks || ""}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default HealthCheck
